<template>
  <template v-if="fetching">
    <ProductSkeletonList v-for="i in 3" :key="i" />
  </template>
  <InnoLineItems :with-header="false" class="variations-default">
    <template #line-items>
      <!-- Innovadis custom BB2B-87 hardcoded added: :show-placed-price="true" to get correct list price -->

      <InnoLineItem
        v-for="variation in variations"
        :key="variation.code"
        :image-url="variation.images[0]?.url"
        :name="getProductName(variation, false)"
        :properties="getProperties(variation)"
        :list-price="variation.price.list"
        :actual-price="variation.price.actual"
        :vendor="$cfg.vendor_enabled ? variation.vendor : undefined"
        :browser-target="$cfg.details_browser_target"
        :with-properties="false"
        :show-placed-price="true"
        :price-unit-text="getProductPriceUnitText(variation)"
        :sales-unit-text="getProductSalesUnitText(variation)"
        :route="getRoute(variation)"
        :product-group-material="
          getCustomPropertyValue(variation.properties, CUSTOM_PROPERTY_GROUP_MATERIAL)?.toString()
        "
        :sku="variation.code"
        with-image
        with-price
      >
        <template #before>
          <VcProductActions :direction="'horizontal'" class="absolute">
            <AddToList :product="variation" />
            <AddToCompareCatalog v-if="$cfg.product_compare_enabled" :product="variation" />
          </VcProductActions>
        </template>

        <AddToCart
          :product="variation"
          :pack-size="variation.packSize"
          :quantity-operators="$cfg.quantity_operators_enabled"
          :show-empty-details="false"
          reserved-space
        >
          <!-- Innovadis custom: only show stock to authenticated users -->
          <InStock
            v-if="isAuthenticated"
            :is-in-stock="variation.availabilityData.isInStock"
            :quantity="variation.availabilityData.availableQuantity"
          />

          <CountInCart :product-id="variation.id" />
        </AddToCart>
      </InnoLineItem>

      <VcPagination
        v-if="pagesCount > 1"
        v-model:page="pageNumber"
        :pages="pagesCount"
        :compact="true"
        class="p-4 align-center order-none flex justify-end md:order-1"
        @update:page="changePage"
      />
    </template>
  </InnoLineItems>
</template>

<script setup lang="ts">
import { sortBy } from "lodash";
import { toRef } from "vue";
import { PropertyType } from "@/core/api/graphql/types";
import { getProductRoute, getPropertiesGroupedByName } from "@/core/utilities";
import { CUSTOM_PROPERTY_GROUP_MATERIAL } from "@/shared/common/constants";
import { AddToCompareCatalog } from "@/shared/compare";
import { AddToList } from "@/shared/wishlists";
import { AddToCart } from "@/shared/cart";
import {
  getProductPriceUnitText,
  getProductSalesUnitText,
  getCustomPropertyValue,
  getProductName,
} from "@/core/utilities/innovadis";
import { useUser } from "@/shared/account";
import { ProductSkeletonList } from "@/shared/catalog/components";
import InStock from "../../in-stock.vue";
import InnoLineItems from "@/ui-kit/components/molecules/innovadis/inno-line-items.vue";
import InnoLineItem from "@/ui-kit/components/molecules/innovadis/inno-line-item.vue";
import CountInCart from "../../count-in-cart.vue";
import type { Product } from "@/core/api/graphql/types";
import { useRouter } from "vue-router";

interface IEmits {
  (event: "changePage", page: number): void;
}

const emit = defineEmits<IEmits>();

const props = defineProps<IProps>();

const { isAuthenticated } = useUser();

interface IProps {
  variations: Product[];
  pageNumber: number;
  pagesCount: number;
  fetching: boolean;
}

const pageNumber = toRef(props, "pageNumber");

function getProperties(variation: Product) {
  return Object.values(
    getPropertiesGroupedByName(sortBy(variation.properties, ["displayOrder", "name"]) ?? [], PropertyType.Variation),
  );
}

// Innovadis custom: use slug from back-end before id (or a wrong url will be shown),
// url decode (or a 404 page will appear) and remove culture prefix (or it will be there twice)
function getRoute(item: Product) {
  const router = useRouter();

  if (item?.id && item?.slug) {
    const rawRoute = getProductRoute(item.slug || item.id);

    const resolved = router.resolve(rawRoute);

    let decodedHref = decodeURIComponent((resolved.params.productId as string) || "");

    // Zorg dat er een leading slash staat
    if (!decodedHref.startsWith("/")) {
      decodedHref = "/" + decodedHref;
    }

    return decodedHref;
  }

  return "";
}

function changePage(page: number): void {
  emit("changePage", page);
}
</script>
<style type="scss">
.variations-default {
  .vc-price-display {
    @apply text-lg;
  }

  .inno-line-item__img {
    @apply mt-5;
  }

  .inno-line-item__name-container {
    @apply w-full;
  }

  .vc-product-price__price-unit {
    @apply text-base font-normal;
  }

  .vc-product-price__sales-unit {
    @apply text-xxs font-normal text-[--color-neutral-500] leading-[1.5rem];
  }

  .price-on-request {
    @apply text-base;
  }
}
</style>
